/**
 * Platform locale registry (M8). Source of truth for locale metadata.
 */
export const PLATFORM_FALLBACK_LOCALE = "es-CO";

export const LOCALE_REGISTRY = {
  "es-CO": {
    language: "es",
    country: "CO",
    label: "Español (Colombia)",
    nativeLabel: "Español (Colombia)",
    urlSegment: "es",
    defaultCurrency: "COP",
    textDirection: "ltr",
    intlFormatLocale: "es-CO",
  },
  "en-US": {
    language: "en",
    country: "US",
    label: "English (United States)",
    nativeLabel: "English (US)",
    urlSegment: "en",
    defaultCurrency: "USD",
    textDirection: "ltr",
    intlFormatLocale: "en-US",
  },
  "es-MX": {
    language: "es",
    country: "MX",
    label: "Español (México)",
    nativeLabel: "Español (México)",
    urlSegment: "es-mx",
    defaultCurrency: "MXN",
    textDirection: "ltr",
    intlFormatLocale: "es-MX",
  },
  "pt-BR": {
    language: "pt",
    country: "BR",
    label: "Português (Brasil)",
    nativeLabel: "Português (Brasil)",
    urlSegment: "pt-br",
    defaultCurrency: "BRL",
    textDirection: "ltr",
    intlFormatLocale: "pt-BR",
  },
  "fr-FR": {
    language: "fr",
    country: "FR",
    label: "Français (France)",
    nativeLabel: "Français",
    urlSegment: "fr",
    defaultCurrency: "EUR",
    textDirection: "ltr",
    intlFormatLocale: "fr-FR",
  },
  "ru-RU": {
    language: "ru",
    country: "RU",
    label: "Russian (Russia)",
    nativeLabel: "Русский",
    urlSegment: "ru",
    defaultCurrency: "RUB",
    textDirection: "ltr",
    intlFormatLocale: "ru-RU",
  },
};

export const REGISTRY_LOCALES = Object.keys(LOCALE_REGISTRY);

const LANGUAGE_ALIASES = {
  es: "es-CO",
  "es-co": "es-CO",
  es_co: "es-CO",
  en: "en-US",
  "en-us": "en-US",
  en_us: "en-US",
  "es-mx": "es-MX",
  es_mx: "es-MX",
  "pt-br": "pt-BR",
  pt_br: "pt-BR",
  pt: "pt-BR",
  fr: "fr-FR",
  "fr-fr": "fr-FR",
  fr_fr: "fr-FR",
  ru: "ru-RU",
  "ru-ru": "ru-RU",
  ru_ru: "ru-RU",
};

export function isRegistryLocale(locale) {
  return REGISTRY_LOCALES.includes(locale);
}

export function getLocaleMetadata(locale) {
  const normalized = normalizeRegistryLocale(locale);
  return LOCALE_REGISTRY[normalized] || LOCALE_REGISTRY[PLATFORM_FALLBACK_LOCALE];
}

export function getLocaleUrlSegment(locale) {
  return getLocaleMetadata(locale).urlSegment;
}

export function getIntlFormatLocale(locale) {
  return getLocaleMetadata(locale).intlFormatLocale;
}

export function normalizeRegistryLocale(value) {
  if (value == null || value === "") {
    return PLATFORM_FALLBACK_LOCALE;
  }
  const lower = String(value).trim().toLowerCase();
  if (LANGUAGE_ALIASES[lower]) {
    return LANGUAGE_ALIASES[lower];
  }
  const exact = REGISTRY_LOCALES.find((locale) => locale.toLowerCase() === lower);
  if (exact) {
    return exact;
  }
  const language = lower.split("-")[0];
  if (LANGUAGE_ALIASES[language]) {
    return LANGUAGE_ALIASES[language];
  }
  return PLATFORM_FALLBACK_LOCALE;
}

/**
 * Resolve a URL segment to a registry locale, optionally constrained by manifest supported locales.
 */
export function resolveUrlSegmentToLocale(segment, manifestI18n = null) {
  if (!segment) return null;
  const lower = String(segment).trim().toLowerCase();
  const supported = manifestI18n?.supportedLocales?.length
    ? manifestI18n.supportedLocales.map(normalizeRegistryLocale)
    : REGISTRY_LOCALES;
  const defaultLocale = normalizeRegistryLocale(manifestI18n?.defaultLocale || PLATFORM_FALLBACK_LOCALE);

  for (const locale of supported) {
    if (getLocaleUrlSegment(locale) === lower) {
      return locale;
    }
  }

  if (lower === "es") {
    const spanish = supported.filter((locale) => locale.startsWith("es-"));
    if (spanish.includes(defaultLocale)) return defaultLocale;
    if (spanish.length) return spanish[0];
    return "es-CO";
  }
  if (lower === "en") {
    const english = supported.filter((locale) => locale.startsWith("en-"));
    if (english.includes(defaultLocale)) return defaultLocale;
    if (english.length) return english[0];
    return "en-US";
  }
  if (lower === "pt" || lower === "pt-br") {
    const portuguese = supported.filter((locale) => locale.startsWith("pt-"));
    if (portuguese.length) return portuguese[0];
    return normalizeRegistryLocale("pt-BR");
  }
  if (lower === "fr") {
    const french = supported.filter((locale) => locale.startsWith("fr-"));
    if (french.length) return french[0];
    return normalizeRegistryLocale("fr-FR");
  }
  if (lower === "ru") {
    const russian = supported.filter((locale) => locale.startsWith("ru-"));
    if (russian.length) return russian[0];
    return normalizeRegistryLocale("ru-RU");
  }

  const alias = LANGUAGE_ALIASES[lower];
  if (alias && supported.includes(alias)) {
    return alias;
  }

  return null;
}

export function localeToOpenGraphLocaleTag(locale) {
  return normalizeRegistryLocale(locale).replace("-", "_");
}
